import type { LearningDomain, Subject } from '@/content/schema';
import { lesson, MOE_REF, objective, SAMPLE, type ObjectiveInput } from '../helpers';

export const subject: Subject = {
  id: 'math',
  name: { ar: 'الرياضيّات', fr: 'Mathématiques', en: 'Mathematics' },
  glyph: '+',
  color: 'math',
  teachingLanguage: 'ar',
  levels: ['prep', 'y1', 'y2', 'y3', 'y4', 'y5', 'y6'],
  sources: MOE_REF,
  ...SAMPLE,
};

export const domains: LearningDomain[] = [
  {
    id: 'math.numbers',
    subjectId: 'math',
    name: { ar: 'الأعداد والعدّ', fr: 'Nombres et comptage', en: 'Numbers and counting' },
  },
  {
    id: 'math.add-sub',
    subjectId: 'math',
    name: { ar: 'الجمع والطرح', fr: 'Addition et soustraction', en: 'Addition and subtraction' },
  },
  {
    id: 'math.mul',
    subjectId: 'math',
    name: { ar: 'الضرب', fr: 'Multiplication', en: 'Multiplication' },
  },
  {
    id: 'math.problems',
    subjectId: 'math',
    name: { ar: 'حلّ المسائل', fr: 'Résolution de problèmes', en: 'Problem solving' },
  },
];

const ARITH_GAMES = ['quick-quiz', 'find-correct', 'true-false', 'memory'] as const;
/** For objectives whose formats include "missing". */
const ARITH_GAMES_MISSING = [...ARITH_GAMES, 'missing-number'] as const;
const o = (input: ObjectiveInput) => objective('math', 'ar', input);

/**
 * Difficulty bands (bands.ts) follow the requested progression 1–10, 10–100,
 * 100–1000 and mixed. Which year uses which band is a sample placement to be
 * validated, as is the year multiplication starts.
 */
export const objectives = [
  o({
    id: 'math.count-10',
    domainId: 'math.numbers',
    levels: ['prep', 'y1'],
    order: 10,
    difficulty: 1,
    title: { ar: 'العدّ من 1 إلى 10', fr: 'Compter de 1 à 10', en: 'Counting from 1 to 10' },
    description: {
      ar: 'يعدّ أشياء حتى 10، ويحدّد مكان العدد على المستقيم العددي، ويجد العدد الموالي.',
      fr: 'Compter des objets jusqu’à 10, situer un nombre sur la droite graduée et trouver le suivant.',
      en: 'Count objects up to 10, place a number on the number line and find the next one.',
    },
    practice: {
      type: 'counting',
      params: { min: 1, max: 10, formats: ['count', 'numberLine', 'nextNumber'] },
    },
    games: ['quick-quiz', 'find-correct'],
  }),
  o({
    id: 'math.add-b1',
    domainId: 'math.add-sub',
    levels: ['y1'],
    order: 20,
    difficulty: 1,
    prerequisites: ['math.count-10'],
    title: { ar: 'الجمع في حدود 10', fr: 'Additionner jusqu’à 10', en: 'Adding within 10' },
    description: {
      ar: 'يجمع عددين مجموعهما لا يتجاوز 10.',
      fr: 'Additionner deux nombres dont la somme ne dépasse pas 10.',
      en: 'Add two numbers whose sum is at most 10.',
    },
    practice: {
      type: 'arithmetic',
      params: {
        operations: ['add'],
        band: 'b1',
        formats: ['input', 'choice', 'numberLine', 'trueFalse', 'match'],
      },
    },
    games: [...ARITH_GAMES],
  }),
  o({
    id: 'math.sub-b1',
    domainId: 'math.add-sub',
    levels: ['y1'],
    order: 30,
    difficulty: 2,
    prerequisites: ['math.add-b1'],
    title: { ar: 'الطرح في حدود 10', fr: 'Soustraire jusqu’à 10', en: 'Subtracting within 10' },
    description: {
      ar: 'يطرح عددًا من عدد لا يتجاوز 10.',
      fr: 'Soustraire un nombre d’un nombre inférieur ou égal à 10.',
      en: 'Subtract from a number up to 10.',
    },
    practice: {
      type: 'arithmetic',
      params: {
        operations: ['sub'],
        band: 'b1',
        formats: ['input', 'choice', 'numberLine', 'trueFalse', 'match'],
      },
    },
    games: [...ARITH_GAMES],
  }),
  o({
    id: 'math.missing-b1',
    domainId: 'math.add-sub',
    levels: ['y1'],
    order: 40,
    difficulty: 2,
    prerequisites: ['math.add-b1', 'math.sub-b1'],
    title: {
      ar: 'العدد الناقص في حدود 10',
      fr: 'Le nombre manquant jusqu’à 10',
      en: 'Missing number within 10',
    },
    description: {
      ar: 'يجد العدد الناقص في عمليّة جمع أو طرح مثل 3 + ؟ = 7.',
      fr: 'Trouver le nombre manquant dans une addition ou une soustraction.',
      en: 'Find the missing number in an addition or subtraction.',
    },
    practice: {
      type: 'arithmetic',
      params: { operations: ['add', 'sub'], band: 'b1', formats: ['missing'] },
    },
    games: ['missing-number', 'quick-quiz'],
  }),
  o({
    id: 'math.add-b2',
    domainId: 'math.add-sub',
    levels: ['y2'],
    order: 50,
    difficulty: 2,
    prerequisites: ['math.add-b1'],
    title: { ar: 'الجمع في حدود 100', fr: 'Additionner jusqu’à 100', en: 'Adding within 100' },
    description: {
      ar: 'يجمع أعدادًا مجموعها بين 10 و100، مع الاحتفاظ أو دونه.',
      fr: 'Additionner des nombres jusqu’à 100, avec ou sans retenue.',
      en: 'Add numbers up to 100, with or without carrying.',
    },
    practice: {
      type: 'arithmetic',
      params: {
        operations: ['add'],
        band: 'b2',
        formats: ['input', 'choice', 'findMistake', 'trueFalse', 'match', 'wordProblem'],
      },
    },
    games: [...ARITH_GAMES],
  }),
  o({
    id: 'math.sub-b2',
    domainId: 'math.add-sub',
    levels: ['y2'],
    order: 60,
    difficulty: 3,
    prerequisites: ['math.sub-b1', 'math.add-b2'],
    title: { ar: 'الطرح في حدود 100', fr: 'Soustraire jusqu’à 100', en: 'Subtracting within 100' },
    description: {
      ar: 'يطرح أعدادًا في حدود 100، بما في ذلك أخذ عشرة من خانة العشرات.',
      fr: 'Soustraire des nombres jusqu’à 100, y compris avec un emprunt.',
      en: 'Subtract numbers up to 100, including exchanging a ten.',
    },
    practice: {
      type: 'arithmetic',
      params: {
        operations: ['sub'],
        band: 'b2',
        formats: ['input', 'choice', 'findMistake', 'trueFalse', 'match', 'wordProblem'],
      },
    },
    games: [...ARITH_GAMES],
  }),
  o({
    id: 'math.missing-b2',
    domainId: 'math.add-sub',
    levels: ['y2'],
    order: 70,
    difficulty: 3,
    prerequisites: ['math.missing-b1', 'math.add-b2'],
    title: {
      ar: 'العدد الناقص في حدود 100',
      fr: 'Le nombre manquant jusqu’à 100',
      en: 'Missing number within 100',
    },
    description: {
      ar: 'يجد العدد الناقص مثل 12 + ؟ = 20.',
      fr: 'Trouver le nombre manquant, par exemple 12 + ? = 20.',
      en: 'Find the missing number, for example 12 + ? = 20.',
    },
    practice: {
      type: 'arithmetic',
      params: { operations: ['add', 'sub'], band: 'b2', formats: ['missing'] },
    },
    games: ['missing-number', 'quick-quiz'],
  }),
  o({
    id: 'math.add-b3',
    domainId: 'math.add-sub',
    levels: ['y3'],
    order: 80,
    difficulty: 3,
    prerequisites: ['math.add-b2'],
    title: { ar: 'الجمع في حدود 1000', fr: 'Additionner jusqu’à 1 000', en: 'Adding within 1,000' },
    description: {
      ar: 'يجمع أعدادًا من ثلاثة أرقام مجموعها لا يتجاوز 1000.',
      fr: 'Additionner des nombres à trois chiffres jusqu’à 1 000.',
      en: 'Add three-digit numbers up to 1,000.',
    },
    practice: {
      type: 'arithmetic',
      params: {
        operations: ['add'],
        band: 'b3',
        formats: ['input', 'choice', 'findMistake', 'trueFalse', 'match'],
      },
    },
    games: [...ARITH_GAMES],
  }),
  o({
    id: 'math.sub-b3',
    domainId: 'math.add-sub',
    levels: ['y3'],
    order: 90,
    difficulty: 3,
    prerequisites: ['math.sub-b2', 'math.add-b3'],
    title: {
      ar: 'الطرح في حدود 1000',
      fr: 'Soustraire jusqu’à 1 000',
      en: 'Subtracting within 1,000',
    },
    description: {
      ar: 'يطرح أعدادًا من ثلاثة أرقام.',
      fr: 'Soustraire des nombres à trois chiffres.',
      en: 'Subtract three-digit numbers.',
    },
    practice: {
      type: 'arithmetic',
      params: {
        operations: ['sub'],
        band: 'b3',
        formats: ['input', 'choice', 'findMistake', 'trueFalse', 'match'],
      },
    },
    games: [...ARITH_GAMES],
  }),
  o({
    id: 'math.mul-2-5',
    domainId: 'math.mul',
    levels: ['y3'],
    order: 100,
    difficulty: 3,
    prerequisites: ['math.add-b2'],
    title: {
      ar: 'جداول الضرب 2 و3 و4 و5',
      fr: 'Tables de 2, 3, 4 et 5',
      en: 'Times tables 2, 3, 4 and 5',
    },
    description: {
      ar: 'يحفظ جداول الضرب من 2 إلى 5 ويفهم أنّ الضرب جمع متكرّر.',
      fr: 'Connaître les tables de 2 à 5 et comprendre la multiplication comme addition répétée.',
      en: 'Know the 2 to 5 times tables and see multiplication as repeated addition.',
    },
    practice: {
      type: 'arithmetic',
      params: {
        operations: ['mul'],
        band: 'b1',
        tables: [2, 3, 4, 5],
        formats: ['input', 'choice', 'trueFalse', 'match', 'missing'],
      },
    },
    games: [...ARITH_GAMES_MISSING],
  }),
  o({
    id: 'math.mul-6-10',
    domainId: 'math.mul',
    levels: ['y4'],
    order: 110,
    difficulty: 4,
    prerequisites: ['math.mul-2-5'],
    title: { ar: 'جداول الضرب من 6 إلى 10', fr: 'Tables de 6 à 10', en: 'Times tables 6 to 10' },
    description: {
      ar: 'يحفظ جداول الضرب من 6 إلى 10.',
      fr: 'Connaître les tables de 6 à 10.',
      en: 'Know the 6 to 10 times tables.',
    },
    practice: {
      type: 'arithmetic',
      params: {
        operations: ['mul'],
        band: 'b1',
        tables: [6, 7, 8, 9, 10],
        formats: ['input', 'choice', 'trueFalse', 'match', 'missing'],
      },
    },
    games: [...ARITH_GAMES_MISSING],
  }),
  o({
    id: 'math.mul-b2',
    domainId: 'math.mul',
    levels: ['y4'],
    order: 120,
    difficulty: 4,
    prerequisites: ['math.mul-6-10'],
    title: {
      ar: 'ضرب عدد من رقمين في عدد من رقم واحد',
      fr: 'Multiplier un nombre à deux chiffres',
      en: 'Two-digit by one-digit multiplication',
    },
    description: {
      ar: 'يضرب عددًا بين 10 و99 في عدد من رقم واحد بالتفكيك.',
      fr: 'Multiplier un nombre de 10 à 99 par un chiffre en décomposant.',
      en: 'Multiply a number from 10 to 99 by a single digit by splitting it.',
    },
    practice: {
      type: 'arithmetic',
      params: {
        operations: ['mul'],
        band: 'b2',
        formats: ['input', 'choice', 'findMistake', 'trueFalse'],
      },
    },
    games: ['quick-quiz', 'find-correct', 'true-false'],
  }),
  o({
    id: 'math.missing-b3',
    domainId: 'math.add-sub',
    levels: ['y4'],
    order: 130,
    difficulty: 4,
    prerequisites: ['math.missing-b2', 'math.add-b3', 'math.sub-b3'],
    title: {
      ar: 'العدد الناقص في حدود 1000',
      fr: 'Le nombre manquant jusqu’à 1 000',
      en: 'Missing number within 1,000',
    },
    description: {
      ar: 'يجد العدد الناقص في عمليّات جمع وطرح بأعداد من ثلاثة أرقام.',
      fr: 'Trouver le nombre manquant avec des nombres à trois chiffres.',
      en: 'Find the missing number with three-digit numbers.',
    },
    practice: {
      type: 'arithmetic',
      params: { operations: ['add', 'sub'], band: 'b3', formats: ['missing'] },
    },
    games: ['missing-number', 'quick-quiz'],
  }),
  o({
    id: 'math.mul-b3',
    domainId: 'math.mul',
    levels: ['y5'],
    order: 140,
    difficulty: 5,
    prerequisites: ['math.mul-b2'],
    title: {
      ar: 'ضرب عدد من ثلاثة أرقام في عدد من رقم واحد',
      fr: 'Multiplier un nombre à trois chiffres',
      en: 'Three-digit by one-digit multiplication',
    },
    description: {
      ar: 'يضرب عددًا من ثلاثة أرقام في عدد من رقم واحد.',
      fr: 'Multiplier un nombre à trois chiffres par un chiffre.',
      en: 'Multiply a three-digit number by a single digit.',
    },
    practice: {
      type: 'arithmetic',
      params: { operations: ['mul'], band: 'b3', formats: ['input', 'choice', 'findMistake'] },
    },
    games: ['quick-quiz', 'find-correct'],
  }),
  o({
    id: 'math.mixed',
    domainId: 'math.add-sub',
    levels: ['y5', 'y6'],
    order: 150,
    difficulty: 4,
    prerequisites: ['math.add-b3', 'math.sub-b3', 'math.mul-6-10'],
    title: { ar: 'تمارين مختلطة', fr: 'Calculs mélangés', en: 'Mixed practice' },
    description: {
      ar: 'جمع وطرح وضرب بأعداد من مستويات مختلفة.',
      fr: 'Additions, soustractions et multiplications de tous niveaux.',
      en: 'Addition, subtraction and multiplication across all ranges.',
    },
    practice: {
      type: 'arithmetic',
      params: {
        operations: ['add', 'sub', 'mul'],
        band: 'mixed',
        formats: ['input', 'choice', 'missing', 'findMistake', 'trueFalse', 'match'],
      },
    },
    games: [...ARITH_GAMES_MISSING],
  }),
  o({
    id: 'math.word-problems',
    domainId: 'math.problems',
    levels: ['y5', 'y6'],
    order: 160,
    difficulty: 4,
    prerequisites: ['math.add-b2', 'math.sub-b2', 'math.mul-2-5'],
    title: {
      ar: 'مسائل من الحياة اليوميّة',
      fr: 'Problèmes de la vie quotidienne',
      en: 'Everyday word problems',
    },
    description: {
      ar: 'يقرأ مسألة قصيرة ويختار العمليّة المناسبة لحلّها.',
      fr: 'Lire un court problème et choisir la bonne opération.',
      en: 'Read a short problem and choose the right operation.',
    },
    practice: {
      type: 'arithmetic',
      params: { operations: ['add', 'sub', 'mul'], band: 'b2', formats: ['wordProblem'] },
    },
    games: ['quick-quiz'],
  }),
];

export const lessons = [
  lesson('math.count-10', 'نعدّ معًا', [
    { type: 'intro', text: 'العدّ يعني أن نُعطي لكلّ شيء عددًا واحدًا، بالترتيب: 1، 2، 3…' },
    {
      type: 'illustration',
      figure: { type: 'numberLine', min: 0, max: 10, marked: 4 },
      caption: 'العدد 4 على المستقيم العددي.',
    },
    {
      type: 'example',
      text: 'هنا 4 تفّاحات. نضع إصبعنا على كلّ تفّاحة ونقول: واحد، اثنان، ثلاثة، أربعة.',
      visual: { emoji: '🍎🍎🍎🍎', label: '4' },
    },
    {
      type: 'rule',
      title: 'قاعدة العدّ',
      text: 'نعدّ كلّ شيء مرّة واحدة فقط، وآخر عدد نقوله هو عدد الأشياء كلّها.',
      figure: { type: 'count', count: 6, emoji: '⭐', label: '6' },
      caption: 'واحد، اثنان، ثلاثة، أربعة، خمسة، ستّة: 6 نجوم.',
      examples: [
        { text: 'نضع إصبعنا على كلّ شيء ونقول عددًا واحدًا فقط.' },
        { text: 'إذا عددنا الشيء نفسه مرّتين أخطأنا: نضع ما عددناه جانبًا.' },
        { text: '🍎🍎🍎: واحد، اثنان، ثلاثة ← 3 تفّاحات.' },
      ],
    },
  ]),
  lesson('math.add-b1', 'نجمع: نضمّ مجموعتين', [
    { type: 'intro', text: 'الجمع يعني أن نضمّ مجموعتين لنعرف كم أصبح العدد.' },
    {
      type: 'illustration',
      figure: { type: 'objects', op: 'add', a: 3, b: 2, emoji: '🎈' },
      caption: '3 + 2 = 5',
    },
    {
      type: 'example',
      text: 'عندي 3 بالونات، وأعطتني أمّي بالونين، فأصبح عندي 5 بالونات.',
      expression: '3 + 2 = 5',
      visual: { emoji: '🎈🎈🎈 + 🎈🎈', label: '3 + 2' },
    },
    {
      type: 'rule',
      title: 'نبدّل العددين في الجمع',
      text: 'إذا بدّلنا مكان العددين في الجمع، لا تتغيّر النتيجة. لذلك نبدأ من العدد الأكبر ونعدّ إلى الأمام.',
      figure: { type: 'objects', op: 'add', a: 2, b: 3, emoji: '🎈' },
      caption: '2 + 3 = 5، و3 + 2 = 5 أيضًا.',
      examples: [
        { expression: '3 + 2 = 2 + 3 = 5' },
        { expression: '1 + 6 = 6 + 1 = 7', text: 'نبدأ من 6 ونعدّ واحدًا: أسهل!' },
        { expression: '4 + 5 = 5 + 4 = 9' },
        { expression: '7 + 0 = 7', text: 'إذا جمعنا 0 لا يتغيّر العدد.' },
      ],
    },
  ]),
  lesson('math.sub-b1', 'نطرح: ماذا بقي؟', [
    { type: 'intro', text: 'الطرح يعني أن نأخذ جزءًا من مجموعة لنعرف كم بقي.' },
    {
      type: 'illustration',
      figure: { type: 'objects', op: 'sub', a: 7, b: 3, emoji: '🍬' },
      caption: 'نشطب 3 من 7، فيبقى 4.',
    },
    { type: 'example', text: 'كان عندي 7 حلويّات، أكلت 3، فبقيت 4.', expression: '7 − 3 = 4' },
    {
      type: 'rule',
      title: 'الطرح عكس الجمع',
      text: 'نتحقّق من الطرح بالجمع: نجمع ما بقي مع ما أخذناه، فنجد العدد الذي بدأنا به.',
      figure: { type: 'bar', total: 7, parts: [3, 4] },
      caption: 'الكلّ 7: أخذنا 3 وبقي 4.',
      examples: [
        { expression: '7 − 3 = 4', text: 'لأنّ 4 + 3 = 7' },
        { expression: '9 − 5 = 4', text: 'لأنّ 4 + 5 = 9' },
        { expression: '6 − 6 = 0', text: 'إذا أخذنا كلّ شيء لا يبقى شيء.' },
        { expression: '8 − 0 = 8', text: 'إذا لم نأخذ شيئًا يبقى العدد كما هو.' },
      ],
    },
    { type: 'tip', text: 'يمكنك أن تبدأ من العدد الأكبر وتعدّ إلى الوراء.' },
  ]),
  lesson('math.missing-b1', 'نبحث عن العدد الناقص', [
    { type: 'intro', text: 'أحيانًا يختفي عدد من العمليّة، ونستطيع أن نجده بالتفكير.' },
    {
      type: 'illustration',
      figure: { type: 'bar', total: 7, parts: [3, null] },
      caption: 'الكلّ 7، والجزء المعروف 3: كم الجزء الآخر؟',
    },
    {
      type: 'example',
      text: 'كم نضيف إلى 3 لنصل إلى 7؟ نعدّ من 3 إلى 7: 4، 5، 6، 7. أضفنا 4.',
      expression: '3 + 4 = 7',
    },
    {
      type: 'rule',
      title: 'كيف نجد العدد الناقص؟',
      text: 'في الجمع: العدد الناقص = الكلّ − الجزء المعروف.',
      figure: { type: 'bar', total: 9, parts: [5, null] },
      caption: 'الكلّ 9 والجزء المعروف 5: 9 − 5 = 4.',
      examples: [
        { expression: '3 + ؟ = 7', text: 'نحسب 7 − 3 = 4' },
        { expression: '؟ + 2 = 8', text: 'نحسب 8 − 2 = 6' },
        { expression: '5 + ؟ = 5', text: 'لا نضيف شيئًا: العدد الناقص 0' },
      ],
    },
    { type: 'tip', text: 'تحقّق دائمًا: ضع العدد مكان الفراغ واحسب.' },
  ]),
  lesson('math.add-b2', 'الجمع في حدود 100', [
    { type: 'intro', text: 'لنجمع عددين من رقمين، نجمع الآحاد مع الآحاد والعشرات مع العشرات.' },
    {
      type: 'illustration',
      figure: { type: 'column', op: 'add', a: 27, b: 15, showResult: true, horizontal: true },
      caption: 'العمليّة نفسها أفقيًّا وعموديًّا. الرقم الصغير فوق هو ما نحتفظ به.',
    },
    {
      type: 'example',
      text: 'الآحاد: 7 + 5 = 12، نكتب 2 ونحتفظ بـ 1. العشرات: 2 + 1 + 1 = 4.',
      expression: '27 + 15 = 42',
    },
    {
      type: 'rule',
      title: 'قانون العقد',
      text: 'كلّ 10 آحاد نربطها في عقدة واحدة فتصبح عشرة. إذا تجاوز مجموع الآحاد 9، نكتب الآحاد ونحتفظ بالعشرة في خانة العشرات.',
      figure: { type: 'bundle', value: 12 },
      caption: '12 وحدة = عقدة واحدة (عشرة) و2 آحاد.',
      examples: [
        { expression: '7 + 5 = 12', text: '10 آحاد تصبح عقدة: نكتب 2 ونحتفظ بـ 1.' },
        { expression: '27 + 15 = 42', text: '7 + 5 = 12: نكتب 2 ونحتفظ بـ 1، ثمّ 2 + 1 + 1 = 4.' },
        { expression: '38 + 46 = 84', text: '8 + 6 = 14: نكتب 4 ونحتفظ بـ 1، ثمّ 3 + 4 + 1 = 8.' },
        { expression: '45 + 23 = 68', text: '5 + 3 = 8 أقلّ من 10: لا نحتفظ بشيء.' },
      ],
    },
  ]),
  lesson('math.sub-b2', 'الطرح في حدود 100', [
    { type: 'intro', text: 'نطرح الآحاد من الآحاد، ثمّ العشرات من العشرات.' },
    {
      type: 'illustration',
      figure: { type: 'column', op: 'sub', a: 52, b: 27, showResult: true, horizontal: true },
      caption: 'الطرح أفقيًّا وعموديًّا.',
    },
    {
      type: 'example',
      text: 'في الآحاد: 2 أصغر من 7، فنأخذ عشرة: 12 − 7 = 5. في العشرات: 4 − 2 = 2.',
      expression: '52 − 27 = 25',
    },
    {
      type: 'tip',
      text: 'لا نطرح الرقم الصغير من الكبير في كلّ خانة دون تفكير: ننظر دائمًا إلى الرقم العلوي.',
    },
    {
      type: 'rule',
      title: 'نفكّ عقدة',
      text: 'إذا كان رقم الآحاد في الأعلى أصغر، نفكّ عقدة من العشرات: عشرة واحدة تصبح 10 آحاد.',
      figure: { type: 'baseTen', value: 52 },
      caption: '52 = 5 عشرات و2 آحاد. نفكّ عشرة: 4 عشرات و12 وحدة.',
      examples: [
        { expression: '52 − 27 = 25', text: '2 أصغر من 7: نفكّ عقدة، 12 − 7 = 5، ثمّ 4 − 2 = 2.' },
        { expression: '40 − 18 = 22', text: '0 أصغر من 8: 10 − 8 = 2، ثمّ 3 − 1 = 2.' },
        { expression: '65 − 23 = 42', text: '5 أكبر من 3: لا نحتاج إلى فكّ عقدة.' },
      ],
    },
  ]),
  lesson('math.missing-b2', 'العدد الناقص في حدود 100', [
    { type: 'intro', text: 'نستعمل العمليّة العكسيّة لنجد العدد الناقص.' },
    {
      type: 'illustration',
      figure: { type: 'bar', total: 20, parts: [12, null] },
      caption: 'الكلّ 20، والجزء المعروف 12.',
    },
    { type: 'example', text: 'في 12 + ؟ = 20 نحسب 20 − 12 = 8.', expression: '12 + 8 = 20' },
    {
      type: 'rule',
      title: 'العمليّة العكسيّة',
      text: 'الجمع والطرح متعاكسان: لنجد العدد الناقص في الجمع نطرح، ولنجد العدد الأوّل في الطرح نجمع.',
      figure: { type: 'bar', total: 20, parts: [12, 8] },
      caption: '12 + 8 = 20، إذن 20 − 12 = 8 و20 − 8 = 12.',
      examples: [
        { expression: '12 + ؟ = 20', text: 'نحسب 20 − 12 = 8' },
        { expression: '؟ − 15 = 30', text: 'نحسب 30 + 15 = 45' },
        { expression: '50 − ؟ = 35', text: 'نحسب 50 − 35 = 15' },
      ],
    },
  ]),
  lesson('math.add-b3', 'الجمع في حدود 1000', [
    { type: 'intro', text: 'نجمع الآحاد، ثمّ العشرات، ثمّ المئات، ولا ننسى ما نحتفظ به.' },
    {
      type: 'illustration',
      figure: { type: 'baseTen', value: 248 },
      caption: 'العدد 248: مئتان وأربع عشرات وثمانية آحاد.',
    },
    {
      type: 'example',
      text: 'الآحاد: 8 + 4 = 12. العشرات: 4 + 7 + 1 = 12. المئات: 2 + 1 + 1 = 4.',
      expression: '248 + 174 = 422',
    },
    {
      type: 'rule',
      title: 'قانون العقد في كلّ الخانات',
      text: '10 آحاد = عشرة واحدة، و10 عشرات = مئة واحدة. نحتفظ في كلّ خانة يتجاوز مجموعها 9.',
      figure: { type: 'column', op: 'add', a: 248, b: 174, showResult: true, horizontal: true },
      examples: [
        {
          expression: '248 + 174 = 422',
          text: 'نحتفظ مرّتين: في الآحاد (12) ثمّ في العشرات (12).',
        },
        { expression: '365 + 240 = 605', text: '6 + 4 = 10 عشرات: نكتب 0 ونحتفظ بمئة.' },
        { expression: '512 + 136 = 648', text: 'لا يتجاوز مجموع أيّ خانة 9: لا نحتفظ بشيء.' },
      ],
    },
    { type: 'tip', text: 'اكتب الأعداد بعضها تحت بعض ورتّب الخانات جيّدًا.' },
  ]),
  lesson('math.sub-b3', 'الطرح في حدود 1000', [
    { type: 'intro', text: 'نطرح خانة بخانة بدءًا من الآحاد.' },
    {
      type: 'illustration',
      figure: { type: 'column', op: 'sub', a: 623, b: 251, showResult: true, horizontal: true },
    },
    {
      type: 'example',
      text: 'الآحاد: 3 − 1 = 2. العشرات: 2 أصغر من 5، فنفكّ مئة إلى 10 عشرات: 12 − 5 = 7. المئات: 5 − 2 = 3.',
      expression: '623 − 251 = 372',
    },
    {
      type: 'rule',
      title: 'نتحقّق من الطرح بالجمع',
      text: 'نجمع النتيجة مع العدد الذي طرحناه: إذا وجدنا العدد الأوّل فالطرح صحيح.',
      figure: { type: 'bar', total: 623, parts: [251, 372] },
      examples: [
        { expression: '623 − 251 = 372', text: 'نتحقّق: 372 + 251 = 623 ✔' },
        { expression: '500 − 125 = 375', text: 'نتحقّق: 375 + 125 = 500 ✔' },
        { expression: '840 − 320 = 520', text: 'نتحقّق: 520 + 320 = 840 ✔' },
      ],
    },
  ]),
  lesson('math.mul-2-5', 'الضرب جمع متكرّر', [
    { type: 'intro', text: 'الضرب طريقة سريعة لجمع العدد نفسه عدّة مرّات.' },
    {
      type: 'illustration',
      figure: { type: 'groups', groups: 3, perGroup: 4, emoji: '✏️', label: '3 × 4' },
      caption: '3 مجموعات، في كلّ مجموعة 4.',
    },
    {
      type: 'example',
      text: '3 علب، في كلّ واحدة منها 4 أقلام: 4 + 4 + 4 = 12.',
      expression: '3 × 4 = 12',
      visual: { emoji: '✏️✏️✏️✏️ | ✏️✏️✏️✏️ | ✏️✏️✏️✏️', label: '3 × 4' },
    },
    {
      type: 'rule',
      title: 'نبدّل العددين في الضرب',
      text: 'ترتيب العددين في الضرب لا يغيّر النتيجة: 3 مجموعات من 4 تساوي 4 مجموعات من 3.',
      figure: { type: 'groups', groups: 4, perGroup: 3, emoji: '✏️', label: '4 × 3' },
      caption: '4 مجموعات، في كلّ مجموعة 3: المجموع 12 أيضًا.',
      examples: [
        { expression: '3 × 4 = 4 × 3 = 12' },
        { expression: '2 × 5 = 5 × 2 = 10' },
        { expression: '5 × 1 = 5', text: 'الضرب في 1 لا يغيّر العدد.' },
        { expression: '5 × 0 = 0', text: 'الضرب في 0 يعطي دائمًا 0.' },
      ],
    },
  ]),
  lesson('math.mul-6-10', 'جداول الضرب الكبيرة', [
    { type: 'intro', text: 'يمكن أن نستعين بجدول نعرفه لنجد نتيجة في جدول أكبر.' },
    {
      type: 'example',
      text: '6 × 7 = (6 × 5) + (6 × 2) = 30 + 12 = 42.',
      expression: '6 × 7 = 42',
    },
    {
      type: 'rule',
      title: 'الضرب في 10',
      text: 'لنضرب عددًا في 10، نكتب صفرًا على يمين العدد: الآحاد تصبح عشرات.',
      figure: { type: 'baseTen', value: 70 },
      caption: '7 × 10 = 70: سبع عشرات.',
      examples: [
        { expression: '7 × 10 = 70' },
        { expression: '10 × 4 = 40' },
        { expression: '23 × 10 = 230' },
      ],
    },
  ]),
  lesson('math.mul-b2', 'نضرب بالتفكيك', [
    { type: 'intro', text: 'نفكّك العدد الكبير إلى عشرات وآحاد ثمّ نضرب كلّ جزء.' },
    {
      type: 'illustration',
      figure: { type: 'column', op: 'mul', a: 23, b: 4, showResult: true, horizontal: true },
    },
    {
      type: 'example',
      text: '23 × 4 = (20 × 4) + (3 × 4) = 80 + 12 = 92.',
      expression: '23 × 4 = 92',
    },
    {
      type: 'rule',
      title: 'نفكّك ثمّ نضرب',
      text: 'نفكّك العدد إلى عشرات وآحاد، ونضرب كلّ جزء، ثمّ نجمع النتيجتين.',
      figure: { type: 'baseTen', value: 23 },
      caption: '23 = 20 + 3',
      examples: [
        { expression: '23 × 4 = 92', text: '(20 × 4) + (3 × 4) = 80 + 12' },
        { expression: '15 × 3 = 45', text: '(10 × 3) + (5 × 3) = 30 + 15' },
        { expression: '42 × 2 = 84', text: '(40 × 2) + (2 × 2) = 80 + 4' },
      ],
    },
  ]),
  lesson('math.missing-b3', 'العدد الناقص في حدود 1000', [
    { type: 'intro', text: 'العمليّة العكسيّة تساعدنا حتى مع الأعداد الكبيرة.' },
    {
      type: 'example',
      text: 'في ؟ − 250 = 400 نحسب 400 + 250 = 650.',
      expression: '650 − 250 = 400',
    },
    { type: 'tip', text: 'تحقّق بوضع العدد في مكانه.' },
  ]),
  lesson('math.mul-b3', 'ضرب الأعداد الكبيرة', [
    { type: 'intro', text: 'نفكّك العدد إلى مئات وعشرات وآحاد.' },
    {
      type: 'example',
      text: '124 × 3 = (100 × 3) + (24 × 3) = 300 + 72 = 372.',
      expression: '124 × 3 = 372',
    },
    {
      type: 'rule',
      title: 'نقدّر النتيجة قبل أن نحسب',
      text: 'نقرّب العدد إلى أقرب عشرة أو مئة لنعرف تقريبًا كم ستكون النتيجة، ثمّ نحسب بدقّة ونقارن.',
      examples: [
        { expression: '124 × 3 = 372', text: 'تقدير: 120 × 3 = 360، والنتيجة قريبة منه ✔' },
        { expression: '198 × 2 = 396', text: 'تقدير: 200 × 2 = 400' },
        { expression: '51 × 4 = 204', text: 'تقدير: 50 × 4 = 200' },
      ],
    },
  ]),
  lesson('math.mixed', 'نختار العمليّة ونحسب', [
    { type: 'intro', text: 'في هذا التمرين تختلط العمليّات: انظر إلى الرمز قبل أن تحسب.' },
    {
      type: 'example',
      text: 'الرمز × يعني الضرب، و− يعني الطرح، و+ يعني الجمع.',
      expression: '45 − 18 = 27',
    },
    { type: 'tip', text: 'خذ وقتك: الدقّة أهمّ من السرعة.' },
  ]),
  lesson('math.word-problems', 'نقرأ المسألة ونفهمها', [
    { type: 'intro', text: 'نقرأ المسألة مرّتين، ثمّ نسأل: هل الكمّيّة تزيد أم تنقص أم تتكرّر؟' },
    {
      type: 'example',
      text: 'في كلّ علبة 6 أقلام، وعندنا 3 علب: الكمّيّة تتكرّر، فنضرب.',
      expression: '3 × 6 = 18',
    },
    {
      type: 'rule',
      title: 'كلمات تدلّ على العمليّة',
      text: 'نبحث في المسألة عن الكلمات التي تدلّ على العمليّة، ثمّ نتحقّق: هل للجواب معنى؟',
      figure: {
        type: 'cards',
        cards: [
          { emoji: '➕', title: 'الجمع', text: 'أضاف، اشترى، معًا' },
          { emoji: '➖', title: 'الطرح', text: 'أعطى، أكل، بقي' },
          { emoji: '✖️', title: 'الضرب', text: 'في كلّ…، مرّات' },
        ],
      },
      examples: [
        { expression: '5 + 3 = 8', text: 'اشترى سامي 5 كراريس ثمّ 3 أخرى: الكمّيّة تزيد.' },
        { expression: '9 − 4 = 5', text: 'كان عند ريم 9 حلويّات وأعطت 4: الكمّيّة تنقص.' },
        { expression: '3 × 6 = 18', text: '3 علب، في كلّ علبة 6 أقلام: الكمّيّة تتكرّر.' },
      ],
    },
  ]),
];

export const items = [];
